import { parseEuroToCents, type ContractListItem, type Lookups, type OrderDetail, type OrderInput, type OrderKind, type OrderListItem, type PdfParseResult } from '@ft/shared';
import { Link } from 'react-router-dom';
import { upload } from '../lib/download';
import { useI18n } from '../i18n';
import { useCallback, useState } from 'react';
import { centsToField, eur, eur2, todayIso } from '../lib/format';
import { api } from '../lib/http';
import { Modal, useLoad } from './ui';

interface Line { key: number; projectId: string; amount: string; hint?: string }
const OWN = '__own__';

export interface PdfPrefill { file: File; result: PdfParseResult }

/**
 * Bestellung anlegen (order = null) oder bearbeiten. Ruft onSaved mit der ID auf.
 * inboxId: Anlegen aus einem Fund im Posteingang – der Server übernimmt die PDF und erledigt den Fund; die Prüfung gegen die PDF ist Pflicht.
 */
export interface QueueNav { pos: number; total: number; onSkip: () => void; onDismiss: () => void }

export function OrderForm({ order, lookups, onClose, onSaved, pdf: pdfProp, inboxId, queue }: { order: OrderDetail | null; lookups: Lookups; onClose: () => void; onSaved: (id: string) => void; pdf?: PdfPrefill; inboxId?: string; queue?: QueueNav }) {
  const [pdf, setPdf] = useState<PdfPrefill | undefined>(pdfProp);
  const [reading, setReading] = useState(false);
  const sg0 = pdfProp?.result.suggestion;
  const sg = pdf?.result.suggestion;
  const { t, lang } = useI18n();
  const [kind, setKind] = useState<OrderKind>(order?.kind ?? 'order');
  const [orderNumber, setOrderNumber] = useState(order?.orderNumber ?? sg0?.orderNumber ?? '');
  const [customerId, setCustomerId] = useState(order?.customer.id ?? sg0?.customerId ?? '');
  const [orderDate, setOrderDate] = useState(order?.orderDate ?? sg0?.orderDate ?? todayIso());
  const [serviceFrom, setServiceFrom] = useState(order?.serviceFrom ?? sg0?.serviceFrom ?? '');
  const [serviceTo, setServiceTo] = useState(order?.serviceTo ?? sg0?.serviceTo ?? '');
  const [note, setNote] = useState(order?.note ?? '');
  const [parentId, setParentId] = useState(order?.parent?.id ?? sg0?.parent?.id ?? '');
  const [checked, setChecked] = useState(false);
  const { data: allOrders } = useLoad<OrderListItem[]>('/api/orders');
  // Hauptverträge (ohne freigeschaltete Seite einfach keine Hinweise)
  const { data: contracts } = useLoad<ContractListItem[]>('/api/contracts');
  const [lines, setLines] = useState<Line[]>(() =>
    order?.lines.length
      ? order.lines.map((l, i) => ({ key: i, projectId: l.project.id, amount: centsToField(l.amountCents) }))
      : sg0?.positions.length
        ? sg0.positions.map((p, i) => ({ key: i, projectId: p.projectId ?? '', amount: centsToField(p.amountCents), hint: p.text || undefined }))
        : [{ key: 0, projectId: '', amount: '' }],
  );
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Bestellwert laut Bestellung (leer = Summe der Verteilung) und nicht freigegebener Teil
  const [totalStr, setTotalStr] = useState(order?.totalSet ? centsToField(order.totalCents) : sg0?.totalCents ? centsToField(sg0.totalCents) : '');
  const [heldStr, setHeldStr] = useState(order?.heldCents ? centsToField(order.heldCents) : '');
  const presetReasons = [t('of.held.r1'), t('of.held.r2'), t('of.held.r3')];
  const [reasonSel, setReasonSel] = useState(() => (!order?.heldReason ? presetReasons[0]! : presetReasons.includes(order.heldReason) ? order.heldReason : OWN));
  const [ownReason, setOwnReason] = useState(order?.heldReason && !presetReasons.includes(order.heldReason) ? order.heldReason : '');
  const [heldUntil, setHeldUntil] = useState(order?.heldUntil ?? '');

  const projects = lookups.projects.filter((p) => p.customerId === customerId);
  // Mögliche ursprüngliche Bestellungen: gleicher Kunde, selbst kein Nachtrag, keine Gewährleistung, nicht diese Bestellung
  const parents = (allOrders ?? []).filter((o) => o.customer.id === customerId && !o.parent && o.kind === 'order' && o.id !== order?.id);
  const hasKids = (order?.amendmentCount ?? 0) > 0;
  const parsed = lines.map((l) => ({ ...l, cents: l.amount.trim() ? parseEuroToCents(l.amount, lang) : null }));
  const total = parsed.reduce((s, l) => s + (l.cents ?? 0), 0);
  const totalC = totalStr.trim() ? parseEuroToCents(totalStr, lang) : null;
  const heldC = heldStr.trim() ? parseEuroToCents(heldStr, lang) : 0;
  const released = totalC === null ? total : totalC - (heldC ?? 0);
  const restRel = released - total;
  const relError = heldC === null || (totalStr.trim() && totalC === null) ? t('of.err.amount')
    : (heldC ?? 0) > 0 && totalC === null ? t('of.err.heldNoTotal')
    : totalC !== null && (heldC ?? 0) > totalC ? t('of.err.heldOver')
    : totalC !== null && restRel < 0 ? t('of.err.overRelease', { over: eur2(-restRel), rel: eur2(released) }) : null;
  // Hinweis je Vertrag: was nach dieser Bestellung noch frei ist (beim Bearbeiten zählt der bisherige Betrag nicht doppelt)
  const contractHints = (() => {
    if (kind !== 'order' || !contracts?.length) return [];
    const inTerm = (c: ContractListItem) => (!c.validFrom || orderDate >= c.validFrom) && (!c.validTo || orderDate <= c.validTo);
    const byContract = new Map<string, { c: ContractListItem; add: number; shares: { name: string; rest: number }[] }>();
    for (const l of parsed) {
      if (!l.projectId || l.cents === null) continue;
      const own = contracts.filter((c) => c.projects.some((p) => p.project.id === l.projectId));
      const c = own.length === 1 ? own[0]! : own.find(inTerm);
      if (!c) continue;
      const before = order?.lines.find((x) => x.project.id === l.projectId)?.amountCents ?? 0;
      const e = byContract.get(c.id) ?? { c, add: 0, shares: [] };
      e.add += l.cents - before;
      const cp = c.projects.find((p) => p.project.id === l.projectId)!;
      if (cp.budgetCents !== null) e.shares.push({ name: cp.project.name, rest: cp.budgetCents - cp.orderedCents - (l.cents - before) });
      byContract.set(c.id, e);
    }
    return [...byContract.values()];
  })();
  const close = useCallback(() => { if (!busy && !reading) onClose(); }, [busy, reading, onClose]);

  /** PDF auswählen: Server liest sie aus, erkannte Werte überschreiben die Felder. */
  async function readPdf(file: File | undefined) {
    if (!file) return;
    setReading(true);
    setError(null);
    try {
      const result = await upload<PdfParseResult>('POST', '/api/orders/parse-pdf', file);
      const x = result.suggestion;
      if (x.orderNumber) setOrderNumber(x.orderNumber);
      if (x.customerId) setCustomerId(x.customerId);
      if (x.orderDate) setOrderDate(x.orderDate);
      if (x.serviceFrom) setServiceFrom(x.serviceFrom);
      if (x.serviceTo) setServiceTo(x.serviceTo);
      if (x.parent) setParentId(x.parent.id);
      if (x.positions.length) setLines(x.positions.map((p, i) => ({ key: Date.now() + i, projectId: p.projectId ?? '', amount: centsToField(p.amountCents), hint: p.text || undefined })));
      setKind('order');
      setPdf({ file, result });
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setReading(false);
    }
  }

  function setLine(key: number, patch: Partial<Line>) { setLines((ls) => ls.map((l) => (l.key === key ? { ...l, ...patch } : l))); }

  async function save() {
    setError(null);
    if (!customerId) return setError(t('of.err.customer'));
    if (kind === 'order' && !orderNumber.trim()) return setError(t('of.err.number'));
    const used = parsed.filter((l) => l.projectId || l.amount.trim());
    if (used.some((l) => !l.projectId)) return setError(t('of.err.project'));
    if (used.some((l) => l.cents === null || l.cents < 0)) return setError(t('of.err.amount'));
    if (!used.length) return setError(t('of.err.lines'));
    if (inboxId && !checked) return setError(t('of.err.checked'));
    if (relError) return setError(relError);
    const reason = (heldC ?? 0) > 0 ? (reasonSel === OWN ? ownReason.trim() : reasonSel) : '';
    if ((heldC ?? 0) > 0 && !reason) return setError(t('of.err.heldReason'));
    const body: OrderInput = {
      version: order?.version,
      orderNumber: orderNumber.trim() || null, kind, customerId, orderDate,
      serviceFrom: serviceFrom || null, serviceTo: serviceTo || null, note: note.trim() || null,
      lines: used.map((l) => ({ projectId: l.projectId, amountCents: l.cents! })),
      parentOrderId: kind === 'order' && parentId ? parentId : null,
      totalCents: totalC, heldCents: heldC ?? 0, heldReason: reason || null, heldUntil: (heldC ?? 0) > 0 && heldUntil ? heldUntil : null,
      ...(!order && pdf && checked ? { checked: true } : {}),
      ...(inboxId ? { inboxId } : {}),
    };
    setBusy(true);
    try {
      const r = order ? await api<{ id: string }>('PUT', `/api/orders/${order.id}`, body) : await api<{ id: string }>('POST', '/api/orders', body);
      if (pdf && !inboxId) await upload('PUT', `/api/orders/${r.id}/pdf`, pdf.file);
      onSaved(r.id);
    } catch (e) {
      setError((e as Error).message);
      setBusy(false);
    }
  }

  return (
    <Modal
      title={order ? t('of.edit', { n: order.orderNumber ?? '' }) : pdf ? (queue ? `${t('pdf.title')} · ${t('queue.pos', { i: queue.pos, n: queue.total })}` : t('pdf.title')) : t('of.new')}
      crumb={t('nav.orders')}
      onClose={close}
      footer={<>
        {error && <p className="err" role="alert">{error}</p>}
        <button className="btn" onClick={close} disabled={busy}>{t('common.cancel')}</button>
        {queue && <button className="btn ghost" onClick={queue.onDismiss} disabled={busy}>{t('inbox.dismiss')}</button>}
        {queue && queue.pos < queue.total && <button className="btn" onClick={queue.onSkip} disabled={busy}>{t('queue.skip')}</button>}
        <button className="btn primary" onClick={save} disabled={busy || reading || !!relError}>{busy ? t('common.saving') : t('common.save')}</button>
      </>}
    >
      <form className="form" onSubmit={(e) => { e.preventDefault(); void save(); }}>
        {!order && (
          <label className={`drop full pdfdrop${reading ? ' busy' : ''}`} onDragOver={(e) => e.preventDefault()} onDrop={(e) => { e.preventDefault(); void readPdf(e.dataTransfer.files[0]); }}>
            {reading ? t('pdf.reading') : pdf ? t('pdf.dropAgain', { file: pdf.file.name }) : t('pdf.drop')}
            <input type="file" accept="application/pdf,.pdf" hidden disabled={reading} onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ''; void readPdf(f); }} />
          </label>
        )}
        {pdf && (
          <div className="notice full">
            <div>{t('pdf.check', { file: pdf.file.name })}</div>
            {sg!.docType === 'offer' && <div><b>{t('pdf.doc.offer')}</b></div>}
            {sg!.docType === 'changeRequest' && <div><b>{t('pdf.doc.changeRequest', { parent: sg!.parentNumber ?? '–' })}</b></div>}
            {sg!.ocr && <div>{t('pdf.ocr')}</div>}
            {sg!.warnings.length > 0 && <ul>{sg!.warnings.map((w) => <li key={w}>{t(`pdf.warn.${w}` as 'pdf.warn.project', { total: sg!.totalCents !== null ? eur(sg!.totalCents) : '–', monthly: sg!.monthlyCents ? eur(sg!.monthlyCents) : '–', parent: sg!.parentNumber ?? '–' })}</li>)}</ul>}
            {pdf.result.existing && <div className="err">{t('pdf.exists')} <Link to={`/bestellungen/${pdf.result.existing.id}`} onClick={onClose}>{t('pdf.openExisting')}</Link></div>}
          </div>
        )}
        <div className="fld">
          <span className="flabel">{t('of.kind')}</span>
          <div className="seg" role="group" aria-label={t('of.kind')}>
            <button type="button" aria-pressed={kind === 'order'} onClick={() => setKind('order')}>{t('common.order')}</button>
            <button type="button" aria-pressed={kind === 'warranty'} onClick={() => setKind('warranty')}>{t('common.warranty')}</button>
          </div>
        </div>
        <div className="fld">
          <label htmlFor="of-no">{t('of.number')}{kind === 'order' && <span className="req"> *</span>}</label>
          <input id="of-no" className="mono" value={orderNumber} onChange={(e) => setOrderNumber(e.target.value.toUpperCase())} placeholder={t('of.numberPh')} autoComplete="off" />
        </div>
        <div className="fld">
          <label htmlFor="of-c">{t('common.customer')} <span className="req">*</span></label>
          <select id="of-c" value={customerId} onChange={(e) => { setCustomerId(e.target.value); setLines((ls) => ls.map((l) => ({ ...l, projectId: '' }))); }}>
            <option value="">{t('common.choose')}</option>
            {lookups.customers.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
        </div>
        <div className="fld">
          <label htmlFor="of-d">{t('of.orderedOn')} <span className="req">*</span></label>
          <input id="of-d" type="date" value={orderDate} onChange={(e) => setOrderDate(e.target.value)} required />
        </div>
        <div className="fld">
          <label htmlFor="of-f">{t('of.from')}</label>
          <input id="of-f" type="date" value={serviceFrom} onChange={(e) => setServiceFrom(e.target.value)} />
        </div>
        <div className="fld">
          <label htmlFor="of-t">{t('of.to')}</label>
          <input id="of-t" type="date" value={serviceTo} onChange={(e) => setServiceTo(e.target.value)} />
        </div>

        {kind === 'order' && !hasKids && (
          <div className="fld full">
            <label htmlFor="of-p">{t('of.parent')}</label>
            <select id="of-p" value={parentId} onChange={(e) => setParentId(e.target.value)} disabled={!customerId}>
              <option value="">{t('of.parentNone')}</option>
              {parents.map((o) => <option key={o.id} value={o.id}>{o.orderNumber ?? t('common.noNumber')} · {o.orderDate}</option>)}
            </select>
            {parentId && <span className="help">{t('of.parentHint')}</span>}
          </div>
        )}

        <div className="fld">
          <label htmlFor="of-total">{t('of.total')}</label>
          <input id="of-total" className={`r${totalStr.trim() && totalC === null ? ' bad' : ''}`} inputMode="decimal" value={totalStr} placeholder={t('of.totalPh')}
            onChange={(e) => setTotalStr(e.target.value)} onBlur={() => { if (totalC !== null) setTotalStr(centsToField(totalC)); }} />
          {sg?.totalCents ? <span className="help">{t('of.totalPdf', { v: eur2(sg.totalCents) })}</span> : <span className="help">{t('of.totalHelp')}</span>}
        </div>
        <div className="fld">
          <label htmlFor="of-held">{t('of.held')}</label>
          <input id="of-held" className={`r${heldC === null ? ' bad' : ''}`} inputMode="decimal" value={heldStr} placeholder="0,00"
            onChange={(e) => setHeldStr(e.target.value)} onBlur={() => { if (heldC) setHeldStr(centsToField(heldC)); }} />
          <span className="help">{t('of.heldHelp')}</span>
        </div>
        {(heldC ?? 0) > 0 && (
          <>
            <div className="fld">
              <label htmlFor="of-hr">{t('of.heldReason')} <span className="req">*</span></label>
              <select id="of-hr" value={reasonSel} onChange={(e) => setReasonSel(e.target.value)}>
                {presetReasons.map((r) => <option key={r} value={r}>{r}</option>)}
                <option value={OWN}>{t('of.held.own')}</option>
              </select>
              {reasonSel === OWN && <input aria-label={t('of.held.own')} value={ownReason} onChange={(e) => setOwnReason(e.target.value)} placeholder={t('of.held.ownPh')} style={{ marginTop: 6 }} autoFocus />}
            </div>
            <div className="fld">
              <label htmlFor="of-hu">{t('of.heldUntil')}</label>
              <input id="of-hu" type="date" value={heldUntil} onChange={(e) => setHeldUntil(e.target.value)} />
              <span className="help">{t('of.heldUntilHelp')}</span>
            </div>
          </>
        )}

        <div className="fld full">
          <span className="flabel">{t('of.alloc')} <span className="req">*</span></span>
          <div className="lines">
            {parsed.map((l, i) => (
              <div className="ln" key={l.key}>
                <select aria-label={t('of.lineProject', { n: i + 1 })} value={l.projectId} onChange={(e) => setLine(l.key, { projectId: e.target.value })} disabled={!customerId}>
                  <option value="">{customerId ? t('of.chooseProject') : t('common.chooseCustomerFirst')}</option>
                  {projects.map((p) => <option key={p.id} value={p.id} disabled={lines.some((x) => x.key !== l.key && x.projectId === p.id)}>{p.name}</option>)}
                </select>
                <input aria-label={t('of.lineAmount', { n: i + 1 })} className={`r${l.amount.trim() && l.cents === null ? ' bad' : ''}`} inputMode="decimal" value={l.amount} placeholder={lang === 'en' ? '0.00' : '0,00'}
                  onChange={(e) => setLine(l.key, { amount: e.target.value })}
                  onBlur={() => { if (l.cents !== null) setLine(l.key, { amount: centsToField(l.cents) }); }} />
                <button type="button" className="btn ghost" aria-label={t('of.lineRemove', { n: i + 1 })} onClick={() => setLines((ls) => ls.length > 1 ? ls.filter((x) => x.key !== l.key) : [{ key: l.key + 1, projectId: '', amount: '' }])}>×</button>
                {l.hint && <span className="lnhint">{t('pdf.lineFrom', { text: l.hint })}</span>}
                {(() => { const sh = contractHints.flatMap((h) => h.shares).find((x) => x.name === projects.find((p) => p.id === l.projectId)?.name); return sh ? <span className={`lnhint${sh.rest < 0 ? ' bad' : ''}`}>{sh.rest < 0 ? t('of.shareOverLine', { over: eur2(-sh.rest) }) : t('of.shareRestLine', { rest: eur2(sh.rest) })}</span> : null; })()}
              </div>
            ))}
            <div className="sum">
              <button type="button" className="btn ghost" style={{ padding: 0 }} disabled={!customerId} onClick={() => setLines((ls) => [...ls, { key: Math.max(...ls.map((x) => x.key)) + 1, projectId: '', amount: '' }])}>{t('of.addProject')}</button>
              <span>{t('of.volume')} <b className="num" style={{ color: 'var(--fg)' }}>{eur2(total)}</b></span>
            </div>
          </div>
          {totalC !== null && (
            <div className="relcheck" role="status">
              <div className="relrows">
                <span>{t('of.total')}</span><b className="num">{eur2(totalC)}</b>
                {(heldC ?? 0) > 0 && <><span>{t('of.minusHeld')}</span><b className="num muted">{eur2(heldC ?? 0)}</b></>}
                <span><b>{t('of.released')}</b></span><b className="num">{eur2(released)}</b>
                <span>{t('of.distributed')}</span><b className="num">{eur2(total)}</b>
                <span>{restRel >= 0 ? t('of.restRel') : t('of.overRel')}</span><b className="num" style={{ color: restRel < 0 ? 'var(--neg)' : restRel > 0 ? 'var(--crit)' : 'var(--pos)' }}>{eur2(Math.abs(restRel))}</b>
              </div>
              <div className="relbar"><i style={{ width: `${Math.min(100, (total / Math.max(totalC, 1)) * 100)}%`, background: restRel < 0 ? 'var(--neg)' : 'var(--accent)' }} /><i style={{ width: `${Math.max(0, (restRel / Math.max(totalC, 1)) * 100)}%`, background: 'var(--sb-light, #b9daf2)' }} /><i className="held" style={{ width: `${((heldC ?? 0) / Math.max(totalC, 1)) * 100}%` }} /></div>
              <div className={`relmsg ${relError ? 'bad' : restRel > 0 ? 'warn' : 'ok'}`}>{relError ?? (restRel > 0 ? t('of.restRelMsg', { rest: eur2(restRel) }) : t('of.relOk'))}</div>
            </div>
          )}
          {contractHints.map(({ c, add, shares }) => {
            const rest = c.restCents === null ? null : c.restCents - add;
            return (
              <span key={c.id} className="help" role="status" style={rest !== null && rest < 0 ? { color: 'var(--neg)' } : undefined}>
                {rest === null ? t('of.contractNoBudget', { name: c.name }) : rest < 0 ? t('of.contractOver', { name: c.name, over: eur2(-rest) }) : t('of.contractRest', { name: c.name, rest: eur2(rest) })}
                {shares.filter((x) => x.rest < 0).map((x) => <span key={x.name}> {t('of.contractShareOver', { project: x.name, over: eur2(-x.rest) })}</span>)}
              </span>
            );
          })}
        </div>

        <div className="fld full">
          <label htmlFor="of-n">{t('of.note')}</label>
          <textarea id="of-n" rows={2} value={note} onChange={(e) => setNote(e.target.value)} placeholder={t('of.notePh')} />
        </div>
        {!order && pdf && (
          <div className="fld full">
            <label className="checkline"><input type="checkbox" checked={checked} onChange={(e) => setChecked(e.target.checked)} />{t('pdf.checked')}{inboxId && <span className="req"> *</span>}</label>
            <span className="help">{t('pdf.checkedHint')}</span>
          </div>
        )}
        <button type="submit" hidden />
      </form>
    </Modal>
  );
}
